/**
 * Agency time zone helpers (Phase 5, D7). Intl only, no date library.
 *
 * Dates are stored in UTC; they are shown and bucketed by day in the agency's IANA time zone
 * (`useAuth().agencyTimezone`, default America/Chicago).
 */

export const FALLBACK_AGENCY_TIMEZONE = 'America/Chicago';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

const DEFAULT_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: 'numeric',
  minute: '2-digit',
};

function toDate(date: Date | string): Date | null {
  const d = date instanceof Date ? date : new Date(date);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** A usable IANA zone: the given one if Intl accepts it, otherwise America/Chicago. */
export function resolveAgencyTimezone(tz: string | null | undefined): string {
  if (!tz) return FALLBACK_AGENCY_TIMEZONE;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return tz;
  } catch {
    return FALLBACK_AGENCY_TIMEZONE;
  }
}

/**
 * Format an instant in the agency time zone.
 * Returns '' for an invalid date.
 * @param opts Intl options; defaults to MM/DD/YYYY, h:mm AM/PM
 */
export function formatInAgencyTz(
  date: Date | string,
  tz: string,
  opts?: Intl.DateTimeFormatOptions
): string {
  const d = toDate(date);
  if (!d) return '';
  return new Intl.DateTimeFormat('en-US', { ...(opts ?? DEFAULT_FORMAT), timeZone: resolveAgencyTimezone(tz) }).format(d);
}

/**
 * The calendar date (YYYY-MM-DD) of an instant in the agency time zone.
 * A date-only string ('2026-03-08') is already a calendar date and is returned unchanged.
 * Returns '' for an invalid date.
 */
export function toAgencyDate(date: Date | string, tz: string): string {
  if (typeof date === 'string' && DATE_ONLY.test(date)) return date;
  const d = toDate(date);
  if (!d) return '';
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: resolveAgencyTimezone(tz),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}
